import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  MessageCircle,
  Minus,
  Plus,
  RotateCcw,
  ShieldCheck,
  Truck,
  X,
  ZoomIn,
} from "lucide-react";

import { CatalogoCard } from "@/components/site/CatalogoCard";
import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { track, trackOnce } from "@/lib/analytics";
import {
  CONFIANZA_COMPRA,
  CATEGORIAS_PRODUCTO,
  TRAMOS_VOLUMEN,
  detallesProducto,
  formatearPrecio,
  imagenesProducto,
  precioUnitario,
  productos,
  type Producto,
  type TramoVolumen,
  whatsappProducto,
} from "@/lib/productos";

function buscarProducto(slug: string) {
  return productos.find((producto) => producto.id === slug);
}

// La ruta es hermana del catálogo para que /productos no necesite actuar como layout.
export const Route = createFileRoute("/productos_/$slug")({
  beforeLoad: ({ params }) => {
    if (!buscarProducto(params.slug)) throw notFound();
  },
  notFoundComponent: ProductoNoEncontrado,
  component: ProductoDetallePage,
  head: ({ params }) => {
    const producto = buscarProducto(params.slug);
    const titulo = producto
      ? `${producto.name} en Chile | Pyme Visible`
      : "Producto no encontrado | Pyme Visible";
    const descripcion = producto
      ? `${producto.description} Precio desde ${formatearPrecio(producto.priceValue)}. Configurado y listo para usar en Chile.`
      : "Revisa el catálogo de productos NFC + QR de Pyme Visible.";
    const canonical = `https://pymevisible.cl/productos/${params.slug}`;

    return {
      meta: [
        { title: titulo },
        { name: "description", content: descripcion },
        { property: "og:title", content: titulo },
        { property: "og:description", content: descripcion },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonical },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: titulo },
        { name: "twitter:description", content: descripcion },
        ...(producto
          ? [
              { property: "og:image", content: `https://pymevisible.cl${producto.image}` },
              { name: "twitter:image", content: `https://pymevisible.cl${producto.image}` },
            ]
          : []),
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: producto
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Product",
                "@id": `${canonical}#product`,
                name: producto.name,
                sku: producto.id,
                category: CATEGORIAS_PRODUCTO.find(
                  (categoria) => categoria.id === producto.category,
                )?.label,
                image: imagenesProducto(producto).map(
                  (imagen) => `https://pymevisible.cl${imagen}`,
                ),
                description: producto.description,
                brand: { "@type": "Brand", name: "Pyme Visible" },
                offers: {
                  "@type": "Offer",
                  seller: { "@type": "Organization", name: "Pyme Visible" },
                  price: producto.priceValue,
                  priceCurrency: "CLP",
                  itemCondition: "https://schema.org/NewCondition",
                  availability:
                    producto.availability === "disponible"
                      ? "https://schema.org/InStock"
                      : "https://schema.org/PreOrder",
                  url: canonical,
                },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Inicio",
                    item: "https://pymevisible.cl/",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: "Productos",
                    item: "https://pymevisible.cl/productos",
                  },
                  { "@type": "ListItem", position: 3, name: producto.name, item: canonical },
                ],
              }),
            },
          ]
        : [],
    };
  },
});

function ProductoDetallePage() {
  const { slug } = Route.useParams();
  const producto = buscarProducto(slug);

  if (!producto) return <ProductoNoEncontrado />;

  return <FichaProducto key={producto.id} producto={producto} />;
}

function ProductoNoEncontrado() {
  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main
        id="contenido"
        className="mx-auto grid min-h-[60vh] max-w-[1320px] place-items-center px-5 py-16 text-center"
      >
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">Productos</p>
          <h1 className="mt-3 text-5xl font-extrabold uppercase leading-none text-ink">
            No encontramos este producto.
          </h1>
          <a
            href="/productos"
            className="pv-button pv-button--primary mt-7 inline-flex min-h-12 items-center gap-2 px-6 py-3"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Volver al catálogo
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function FichaProducto({ producto }: { producto: Producto }) {
  const [tramo, setTramo] = useState<TramoVolumen>(TRAMOS_VOLUMEN[0]!);
  const detalles = useMemo(() => detallesProducto(producto), [producto]);
  const categoria = CATEGORIAS_PRODUCTO.find((item) => item.id === producto.category);
  const relacionados = useMemo(
    () =>
      productos
        .filter((item) => item.id !== producto.id)
        .sort(
          (a, b) =>
            Number(b.category === producto.category) - Number(a.category === producto.category),
        )
        .slice(0, 4),
    [producto],
  );
  const unitario = precioUnitario(producto, tramo);
  const totalSinDescuento = producto.priceValue * tramo.cantidad;
  const total = unitario * tramo.cantidad;
  const whatsapp = whatsappProducto(producto, tramo.cantidad);

  useEffect(() => {
    trackOnce(`view-item:${producto.id}`, "view_item", {
      producto: producto.name,
      categoria: producto.category,
      valor: producto.priceValue,
      moneda: "CLP",
    });
  }, [producto]);

  const registrarCompra = () => {
    track("product_cta_click", {
      producto: producto.name,
      ubicacion: "ficha_producto",
      cantidad: tramo.cantidad,
    });
    track("whatsapp_click", {
      ubicacion: "ficha_producto",
      producto: producto.name,
      cantidad: tramo.cantidad,
    });
  };

  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido" className="pb-24 lg:pb-0">
        <section className="pv-product-detail-section border-b border-ink/10">
          <div className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 sm:py-12 lg:px-10">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1.08fr)_minmax(0,.82fr)_minmax(290px,.68fr)] lg:grid-rows-[auto_1fr] lg:items-start lg:gap-x-7 lg:gap-y-0 xl:gap-x-10">
              <div className="lg:row-span-2">
                <GaleriaProducto producto={producto} />
              </div>

              <div className="lg:col-start-2 lg:row-start-1">
                <nav
                  aria-label="Ruta de navegación"
                  className="flex flex-wrap items-center gap-2 text-xs text-slate"
                >
                  <a href="/" className="transition-colors hover:text-ink">
                    Inicio
                  </a>
                  <span aria-hidden="true">/</span>
                  <a href="/productos" className="transition-colors hover:text-ink">
                    Productos
                  </a>
                  <span aria-hidden="true">/</span>
                  <span className="text-ink">{producto.name}</span>
                </nav>

                <p className="mt-5 font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary sm:mt-7">
                  {categoria?.label}
                </p>
                <h1 className="mt-3 text-4xl font-extrabold uppercase leading-[0.92] text-ink sm:text-5xl">
                  {producto.name}
                </h1>
                <p className="mt-4 text-base leading-relaxed text-slate sm:mt-5">
                  {producto.description}
                </p>
                {producto.category === "stands" && (
                  <a
                    href="/casos"
                    onClick={() =>
                      track("case_study_click", {
                        ubicacion: "ficha_producto",
                        producto: producto.name,
                      })
                    }
                    className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary underline underline-offset-4"
                  >
                    Ver stands NFC instalados en negocios reales
                  </a>
                )}
              </div>

              <aside className="pv-purchase-box rounded-[1.5rem] p-4 sm:rounded-[2rem] sm:p-6 lg:sticky lg:top-32 lg:col-start-3 lg:row-span-2 lg:row-start-1">
                <p className="font-mono text-[0.7rem] uppercase tracking-[0.16em] text-primary">
                  Precio seleccionado
                </p>
                {tramo.descuento > 0 && (
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-sm text-slate line-through">
                      {formatearPrecio(totalSinDescuento)}
                    </span>
                    <span className="rounded-full bg-sky px-2.5 py-1 text-xs font-bold text-primary">
                      {tramo.descuento}% de descuento
                    </span>
                  </div>
                )}
                <p className="mt-1 text-4xl font-extrabold leading-none text-ink">
                  {formatearPrecio(total)}
                </p>
                <p className="mt-2 text-xs text-slate">
                  {tramo.cantidad === 1
                    ? "Precio por unidad"
                    : `${formatearPrecio(unitario)} por unidad · ${tramo.cantidad} unidades`}
                </p>

                <div className="mt-5 sm:mt-7">
                  <h2 className="text-sm font-bold text-ink">Cantidad y precio por volumen</h2>
                  <div
                    className="mt-3 grid grid-cols-3 gap-2 lg:grid-cols-1"
                    role="radiogroup"
                    aria-label="Seleccionar cantidad"
                  >
                    {TRAMOS_VOLUMEN.map((opcion) => {
                      const seleccionado = opcion.cantidad === tramo.cantidad;
                      const precioOpcion = precioUnitario(producto, opcion);
                      return (
                        <button
                          key={opcion.cantidad}
                          type="button"
                          role="radio"
                          aria-checked={seleccionado}
                          onClick={() => setTramo(opcion)}
                          className={`flex min-h-20 flex-col justify-between gap-2 rounded-xl border px-3 py-3 text-left transition-colors lg:grid lg:min-h-14 lg:grid-cols-[1fr_auto] lg:items-center lg:gap-4 lg:px-4 ${
                            seleccionado
                              ? "border-primary bg-sky"
                              : "border-ink/10 bg-white/80 hover:border-primary/50"
                          }`}
                        >
                          <span>
                            <span className="block text-sm font-bold text-ink">
                              {opcion.cantidad} {opcion.cantidad === 1 ? "unidad" : "unidades"}
                            </span>
                            {opcion.descuento > 0 && (
                              <span className="mt-0.5 block text-xs font-semibold text-primary">
                                Ahorra {opcion.descuento}%
                              </span>
                            )}
                          </span>
                          <span className="text-sm font-bold text-ink">
                            {formatearPrecio(precioOpcion)} c/u
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={registrarCompra}
                  className="pv-product-cta mt-6 hidden min-h-14 w-full items-center justify-center gap-2 rounded-full px-5 py-3.5 text-center text-base font-bold text-white lg:inline-flex"
                >
                  <MessageCircle className="h-5 w-5" aria-hidden="true" /> Comprar por WhatsApp
                </a>

                <details className="pv-detail-disclosure group relative z-30 mt-5 rounded-2xl sm:mt-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-4 font-bold text-ink [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-2">
                      <Truck className="h-5 w-5 shrink-0" aria-hidden="true" />
                      Entrega gratis en Santiago
                    </span>
                    <ChevronDown
                      className="h-5 w-5 shrink-0 text-primary transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <div className="pv-delivery-popover absolute bottom-[calc(100%+0.75rem)] left-0 w-full rounded-2xl p-4">
                    <p className="text-xs leading-relaxed text-slate">
                      Entrega habitual en 1 día hábil dentro de nuestra zona urbana central. Los
                      pedidos confirmados antes de las 13:00 pueden entregarse el mismo día, según
                      disponibilidad y ruta.
                    </p>
                    <p className="mt-2 text-xs leading-relaxed text-slate">
                      Sectores alejados —por ejemplo, San Bernardo o Padre Hurtado— se cotizan o se
                      coordinan en un punto de encuentro.
                    </p>
                  </div>
                </details>

                <details className="pv-accordion-row group mt-5 border-t border-ink/10 sm:mt-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-5 text-sm font-bold text-ink [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center gap-2">
                      <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" /> Cómo
                      trabajamos
                    </span>
                    <ChevronDown
                      className="h-5 w-5 shrink-0 text-primary transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-xl bg-ink/10 pb-px">
                    {CONFIANZA_COMPRA.map((item) => (
                      <li key={item.titulo} className="bg-canvas p-3">
                        <p className="text-xs font-bold leading-snug text-ink">{item.titulo}</p>
                        <p className="mt-1 text-[0.68rem] leading-relaxed text-slate">
                          {item.texto}
                        </p>
                      </li>
                    ))}
                  </ul>
                </details>
              </aside>

              <div className="lg:col-start-2 lg:row-start-2 lg:pt-8">
                <div>
                  <h2 className="text-xl font-bold text-ink">Qué incluye</h2>
                  <ul className="mt-4 grid gap-3">
                    {detalles.caracteristicas.map((caracteristica) => (
                      <li
                        key={caracteristica}
                        className="flex gap-3 text-sm leading-relaxed text-slate"
                      >
                        <Check
                          className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                          aria-hidden="true"
                        />
                        {caracteristica}
                      </li>
                    ))}
                  </ul>
                </div>

                <details className="pv-accordion-row group mt-6 border-t border-ink/10">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 [&::-webkit-details-marker]:hidden">
                    <h2 className="text-xl font-bold text-ink">Especificaciones</h2>
                    <ChevronDown
                      className="h-5 w-5 shrink-0 text-primary transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <dl className="-mt-1 divide-y divide-ink/10 pb-1 text-sm">
                    {detalles.especificaciones.map(({ etiqueta, valor }) => (
                      <div key={etiqueta} className="grid grid-cols-[7.5rem_1fr] gap-4 py-3">
                        <dt className="font-semibold text-ink">{etiqueta}</dt>
                        <dd className="text-slate">{valor}</dd>
                      </div>
                    ))}
                  </dl>
                </details>
              </div>
            </div>
          </div>
        </section>

        <section className="pv-related-section" aria-labelledby="productos-relacionados">
          <div className="mx-auto max-w-[1320px] px-5 py-8 sm:px-8 sm:py-16 lg:px-10">
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
              Sigue explorando
            </p>
            <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
              <h2
                id="productos-relacionados"
                className="text-3xl font-extrabold uppercase leading-none text-ink sm:text-5xl"
              >
                Productos relacionados
              </h2>
              <a
                href="/productos"
                className="text-sm font-semibold text-ink underline underline-offset-4"
              >
                Ver catálogo completo
              </a>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 sm:mt-8 sm:gap-5 lg:grid-cols-4">
              {relacionados.map((relacionado) => (
                <CatalogoCard key={relacionado.id} producto={relacionado} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <div className="pv-mobile-buybar fixed inset-x-0 bottom-0 z-40 border-t border-ink/10 p-3 backdrop-blur lg:hidden">
        <a
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          onClick={registrarCompra}
          className="pv-product-cta mx-auto flex min-h-14 max-w-xl items-center justify-center gap-2 rounded-full px-5 py-3.5 text-center text-base font-bold text-white"
        >
          <MessageCircle className="h-5 w-5" aria-hidden="true" /> Comprar por WhatsApp
        </a>
      </div>

      <Footer />
    </div>
  );
}

function GaleriaProducto({ producto }: { producto: Producto }) {
  const imagenes = imagenesProducto(producto);
  const [seleccionada, setSeleccionada] = useState(0);
  const [imagenAmpliada, setImagenAmpliada] = useState(false);

  useEffect(() => setSeleccionada(0), [producto.id]);

  const imagen = imagenes[Math.min(seleccionada, imagenes.length - 1)]!;
  const esAmbiental = imagen.includes("/clientes/") || imagen.includes("ambiente");
  const escalaImagen =
    producto.imageZoom === "keychain" && !esAmbiental
      ? "scale-[1.18] group-hover:scale-[1.3]"
      : "scale-100 group-hover:scale-[1.08]";

  return (
    <div className="grid grid-cols-[4rem_minmax(0,1fr)] gap-3 sm:grid-cols-[4.75rem_minmax(0,1fr)] sm:gap-4">
      <div
        className="pv-scroll grid content-start gap-2 overflow-y-auto"
        aria-label="Galería de imágenes"
      >
        {imagenes.map((miniatura, indice) => {
          const activa = indice === seleccionada;
          const miniaturaAmbiental =
            miniatura.includes("/clientes/") || miniatura.includes("ambiente");
          return (
            <button
              key={`${miniatura}-${indice}`}
              type="button"
              onClick={() => setSeleccionada(indice)}
              aria-label={`Ver imagen ${indice + 1} de ${producto.name}`}
              aria-current={activa}
              className={`aspect-square overflow-hidden rounded-xl border-2 bg-white transition-colors ${
                activa ? "border-primary" : "border-transparent hover:border-primary/40"
              }`}
            >
              <ResponsiveImage
                sizes="(max-width: 639px) 18vw, 100px"
                src={miniatura}
                alt=""
                width={160}
                height={160}
                loading={indice === 0 ? "eager" : "lazy"}
                className={`h-full w-full ${miniaturaAmbiental ? "object-cover" : "object-contain"}`}
              />
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setImagenAmpliada(true)}
        aria-label={`Ampliar ${producto.name}, imagen ${seleccionada + 1}`}
        className={`pv-gallery-main group relative aspect-square overflow-hidden rounded-[2rem] ${producto.imageSurface ?? "bg-mist"}`}
      >
        <ResponsiveImage
          sizes="(max-width: 639px) 90vw, (max-width: 1023px) 70vw, 560px"
          fetchPriority="high"
          src={imagen}
          alt={`${producto.name}, imagen ${seleccionada + 1} de ${imagenes.length}`}
          width={1200}
          height={1200}
          className={`h-full w-full transition-transform duration-700 ease-out ${
            esAmbiental ? "object-cover" : "object-contain"
          } ${escalaImagen}`}
        />
        <span className="pointer-events-none absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-ink/80 px-3 py-2 text-xs font-semibold text-white opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-visible:opacity-100">
          <ZoomIn className="h-4 w-4" aria-hidden="true" /> Toca para ampliar
        </span>
      </button>

      {imagenAmpliada && (
        <ModalImagenProducto
          producto={producto}
          imagen={imagen}
          numero={seleccionada + 1}
          total={imagenes.length}
          onClose={() => setImagenAmpliada(false)}
        />
      )}
    </div>
  );
}

function ModalImagenProducto({
  producto,
  imagen,
  numero,
  total,
  onClose,
}: {
  producto: Producto;
  imagen: string;
  numero: number;
  total: number;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const [posicion, setPosicion] = useState({ x: 0, y: 0 });
  const arrastre = useRef<{
    puntero: number;
    x: number;
    y: number;
    origenX: number;
    origenY: number;
  } | null>(null);
  const dialogoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const overflowAnterior = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogoRef.current?.focus();
    const cerrarConEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", cerrarConEscape);
    return () => {
      document.removeEventListener("keydown", cerrarConEscape);
      document.body.style.overflow = overflowAnterior;
    };
  }, [onClose]);

  const fijarZoom = (nuevoZoom: number) => {
    const limitado = Math.max(1, Math.min(3, nuevoZoom));
    setZoom(limitado);
    if (limitado === 1) setPosicion({ x: 0, y: 0 });
  };

  const cambiarZoom = (delta: number) => {
    setZoom((zoomActual) => {
      const nuevoZoom = Math.max(1, Math.min(3, zoomActual + delta));
      if (nuevoZoom === 1) setPosicion({ x: 0, y: 0 });
      return nuevoZoom;
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] grid bg-ink/90 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label={`Imagen ampliada de ${producto.name}`}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogoRef}
        tabIndex={-1}
        className="relative mx-auto flex h-[calc(100dvh-1.5rem)] w-full max-w-6xl flex-col outline-none sm:h-[calc(100dvh-3rem)]"
      >
        <div className="flex items-center justify-between gap-3 pb-3 pr-14 text-white sm:pr-28">
          <div className="min-w-0">
            <p className="truncate text-sm font-bold">{producto.name}</p>
            <p className="text-xs text-white/70">
              Imagen {numero} de {total} · Zoom {Math.round(zoom * 100)}%
            </p>
            <p className="mt-0.5 text-[0.68rem] font-semibold text-white sm:hidden">
              Toca la X para volver
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar imagen ampliada y volver al producto"
          className="pv-image-modal-close fixed z-30 inline-flex min-h-12 min-w-12 items-center justify-center gap-2 rounded-full bg-white px-3 text-sm font-extrabold text-ink shadow-2xl ring-1 ring-white/70 transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:px-4"
          style={{
            top: "max(0.75rem, env(safe-area-inset-top))",
            right: "max(0.75rem, env(safe-area-inset-right))",
          }}
        >
          <X className="h-6 w-6" aria-hidden="true" />
          <span className="hidden sm:inline">Cerrar</span>
        </button>

        <div
          className={`relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-2xl bg-black/25 ${zoom > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}`}
          onWheel={(event) => {
            event.preventDefault();
            cambiarZoom(event.deltaY < 0 ? 0.25 : -0.25);
          }}
          onDoubleClick={() => fijarZoom(zoom === 1 ? 2 : 1)}
          onPointerDown={(event) => {
            if (zoom === 1) return;
            arrastre.current = {
              puntero: event.pointerId,
              x: event.clientX,
              y: event.clientY,
              origenX: posicion.x,
              origenY: posicion.y,
            };
            event.currentTarget.setPointerCapture(event.pointerId);
          }}
          onPointerMove={(event) => {
            const estado = arrastre.current;
            if (!estado || estado.puntero !== event.pointerId) return;
            setPosicion({
              x: estado.origenX + event.clientX - estado.x,
              y: estado.origenY + event.clientY - estado.y,
            });
          }}
          onPointerUp={(event) => {
            if (arrastre.current?.puntero !== event.pointerId) return;
            arrastre.current = null;
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
              event.currentTarget.releasePointerCapture(event.pointerId);
            }
          }}
          onPointerCancel={() => {
            arrastre.current = null;
          }}
          style={{ touchAction: "none" }}
        >
          <img
            src={imagen}
            alt={`${producto.name}, imagen ampliada ${numero} de ${total}`}
            draggable={false}
            className="max-h-full max-w-full select-none object-contain transition-transform duration-150"
            style={{
              transform: `translate3d(${posicion.x}px, ${posicion.y}px, 0) scale(${zoom})`,
            }}
          />
        </div>
        <div className="absolute inset-x-3 bottom-3 z-10 flex flex-wrap items-center justify-center gap-2 rounded-2xl bg-ink/75 p-2 text-white backdrop-blur-sm sm:left-1/2 sm:right-auto sm:w-max sm:max-w-[calc(100%-1.5rem)] sm:-translate-x-1/2">
          <button
            type="button"
            onPointerDown={(event) => {
              event.stopPropagation();
              cambiarZoom(-0.5);
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (event.detail === 0) cambiarZoom(-0.5);
            }}
            disabled={zoom === 1}
            aria-label="Alejar imagen"
            className="grid h-11 w-11 place-items-center rounded-full bg-white/15 transition-colors hover:bg-white/25 disabled:opacity-40"
          >
            <Minus className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onPointerDown={(event) => {
              event.stopPropagation();
              cambiarZoom(0.5);
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (event.detail === 0) cambiarZoom(0.5);
            }}
            disabled={zoom === 3}
            aria-label="Acercar imagen"
            className="grid h-11 w-11 place-items-center rounded-full bg-white/15 transition-colors hover:bg-white/25 disabled:opacity-40"
          >
            <Plus className="h-5 w-5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onPointerDown={(event) => {
              event.stopPropagation();
              fijarZoom(1);
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (event.detail === 0) fijarZoom(1);
            }}
            aria-label="Restablecer zoom"
            className="inline-flex min-h-11 items-center gap-2 rounded-full bg-white/15 px-4 text-sm font-semibold transition-colors hover:bg-white/25"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Restablecer
          </button>
          <p className="hidden max-w-xs text-xs text-white/70 md:block">
            Usa los controles para acercar y arrastra la imagen para recorrer sus detalles.
          </p>
        </div>
      </div>
    </div>,
    document.body,
  );
}
